export interface WeeklyBalanceChat {
  /** Seeded as the first (user) bubble. */
  userMessage: string;
  /** NutriCoach reply seeded as the agent message that fake-streams in. */
  reply: string;
  /** Natural-language context silently prepended to the user's first reply so
   *  the agent knows which weekly balance explainer opened the chat. */
  contextPrefix: string;
}

/**
 * Builds the scripted exchange shown when the user taps a weekly balance
 * explainer on the overview. Mirrors the contextual
 * entry-point pattern (see `build-adjust-plan-chat.ts`): a fixed user question
 * plus a warm, reassuring NutriCoach explanation that fake-streams in, and a
 * hidden context prefix that rides on the user's first real follow-up.
 */
export type WeeklyBalanceVariant = "on-plan" | "weekly-deficit";

export const buildWeeklyBalanceChat = (
  variant: WeeklyBalanceVariant = "weekly-deficit",
): WeeklyBalanceChat =>
  variant === "on-plan" ? buildOnPlanBalanceChat() : buildWeeklyDeficitChat();

const buildWeeklyDeficitChat = (): WeeklyBalanceChat => ({
  userMessage: "Help me understand this please ☺️",
  reply:
    "Of course. This is your weekly calorie deficit 💜\n\n" +
    "Your daily calorie target already includes a 500 kcal deficit, so we don’t take another 500 kcal off it. Across seven days, those daily deficit chunks add up to your 3,500 kcal weekly target.\n\n" +
    "Each day, we compare your calories with that day’s target. For example, if Monday’s target is 2,000 kcal and you have 2,500 kcal, that’s 500 kcal over target, so your weekly deficit moves from 3,500 to 3,000 kcal. Finishing below target increases the weekly deficit by the difference.\n\n" +
    "The green zone runs from 2,500 to 4,500 kcal, so there’s room for real life while you stay on track.\n\n" +
    "Between 501 and 2,499 kcal, you’re still in a deficit and still making progress. At 500 kcal or below, progress will be much slower.\n\n" +
    "Between 4,501 and 5,500 kcal, your deficit is getting larger than we want. Above 5,500 kcal, it’s too large and can mean more hunger, less energy and a plan that feels harder than it needs to.\n\n" +
    "So the aim isn’t perfection. This helps you understand your progress across the week, rather than judging each day on its own.\n\n" +
    "If you have a few Hobnobs after a stressful day, or a last-minute catch-up turns into a couple of glasses of prosecco, that’s fine. " +
    "Life happens, and it’s supposed to. This range helps you enjoy those moments without feeling like you’ve gone off track 💫",
  contextPrefix:
    "[Context: the user tapped the weekly calorie deficit explainer on the overview. Their " +
    "daily calorie target already includes a 500 kcal deficit and must not have another 500 " +
    "kcal subtracted from it. Seven daily deficit chunks make the 3,500 kcal weekly target. " +
    "If a 2,000 kcal day finishes at 2,500 kcal, the 500 kcal overage moves the weekly deficit " +
    "to 3,000 kcal. The green range is 2,500–4,500 kcal, 500 kcal or below means slower " +
    "progress, and above 5,500 kcal means the deficit is too large. They may have a follow-up " +
    "question about it.]",
});

const buildOnPlanBalanceChat = (): WeeklyBalanceChat => ({
  userMessage: "Help me understand this please ☺️",
  reply:
    "Of course. This is your weekly On Plan range 💜\n\n" +
    "It shows where you’re tracking across the week, not just today.\n\n" +
    "The green zone means you’re in the right place for your goal.\n\n" +
    "If you’re too far under, around -500 or more, you’re pushing the deficit harder than we want. " +
    "That can mean more hunger, less energy and a plan that feels harder than it needs to.\n\n" +
    "If you’re over, that doesn’t automatically mean you’re off plan. Even at +1000, you’re still in a deficit across the week.\n\n" +
    "So the aim isn’t perfection. This helps you understand your progress across the week, rather than judging each day on its own.\n\n" +
    "So if you have a few Hobnobs after a stressful day, or a last-minute catch-up turns into a couple of glasses of prosecco, that’s fine. " +
    "Life happens, and it’s supposed to. This range helps you enjoy those moments without feeling like you’ve gone off track 💫",
  contextPrefix:
    "[Context: the user tapped the info button on their weekly On Plan calorie range on the " +
    "overview, and was shown an explanation of what the range means and that finishing the " +
    "week between roughly -500 and +1000 kcal still counts as on plan. They may have a " +
    "follow-up question about it.]",
});
